<template>
  <label
    :for="`${name}-${value}`"
    class="picker-item"
    :class="{
      'picker-item--active': active,
      'picker-item--stripe': stripe,
      'picker-item--bg': bg,
      'picker-item--image': image && image.url,
      skeleton: skeleton,
    }"
    :tabindex="active ? '0' : '-1'"
    :title="label ? label : value"
    data-cy-picker-item
    @mouseenter="$emit('mouseenter', $event)"
    @mouseleave="$emit('mouseleave', $event)"
    @click="$emit('click', $event)"
  >
    <input
      :id="`${name}-${value}`"
      :type="cancellable ? 'checkbox' : 'radio'"
      :name="name"
      :value="value"
      :checked="active"
      :aria-checked="active"
      @input="set(value)"
    />
    <span
      v-if="!skeleton"
      :class="{ 'picker-item__bg': bg }"
      :style="spanStyle"
    >
      <span
        v-if="image && image.url"
        class="picker-item__image d-block position-relative"
      >
        <lazy-image :image="image" provider="sanity" />
        <icon name="check-circle" />
      </span>

      <span :class="{ 'visually-hidden': bg }">{{
        label ? label : value
      }}</span>

      <i v-if="hasNotif" class="picker-item__notif" />
    </span>
  </label>
</template>

<script>
import LazyImage from '@/components/Image/LazyImage'
import Icon from '@/components/icons/Icon'
import { normalizeUrl } from '@/utils/normalize'

export default {
  name: 'PickerItem',
  components: {
    LazyImage,
    Icon,
  },
  props: {
    /**
     * la valeur associée
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * le label a affiché à la place de la value
     */
    label: {
      type: String,
      default: undefined,
    },
    /**
     * Le nom associé à l'input radio
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Une image de fond au lieu d'un texte
     */
    bg: {
      type: String,
      default: undefined,
    },
    /**
     * Une image au dessus du label
     */
    image: {
      type: Object,
      default: undefined,
    },
    /**
     * Est-ce que l'élément est sélectionné
     */
    active: {
      type: Boolean,
      default: false,
    },
    /**
     * Est-ce que l'élément sélectionné a une information à afficher
     */
    hasNotif: {
      type: Boolean,
      default: false,
    },
    /**
     * Le composant s'affiche dans un état rayé
     */
    stripe: {
      type: Boolean,
      default: false,
    },
    /**
     * Le composant est-il désactivable ?
     */
    cancellable: { type: Boolean, default: false },
    /**
     * Le composant est affiché en squelette de chargement
     */
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    spanStyle() {
      if (this.bg && this.bg !== '') {
        return {
          backgroundImage: `url(${normalizeUrl(this.bg)})`,
        }
      }

      return {}
    },
  },
  methods: {
    set() {
      /**
       * L'événement input retourne la valeur de l'input
       *
       * @event input
       */
      this.$emit('input', this.value)
    },
  },
}
</script>

<style lang="scss">
.picker-item {
  $size: 38px;

  --line-color: transparent;
  --line-position: 0;

  min-width: rem($size);
  min-height: rem($size);
  font-size: var(--h6-font-size);
  font-family: var(--secondary-font-family);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: rem($spacing);
  transition: all 0.2s;
  position: relative;
  background-size: cover;
  background-position: center;
  border-radius: 20px;
  padding: 0 rem(5px);
  border: 2px solid var(--line-color);

  &:focus {
    outline: none;
  }

  &:not(.picker-item--active) {
    @include on-hover-and-focus {
      --line-color: var(--secondary-color);
      --line-position: #{rem(-4px)};
    }
  }

  input {
    @include visually-hidden;
  }

  span {
    position: relative;
  }

  &__bg {
    position: relative;
    display: block;
    width: rem(26px);
    height: rem(26px);
    border-radius: 50%;
    background-color: var(--secondary-color-5);
    background-size: cover;

    &::after {
      content: '';
      position: absolute;
      inset: var(--line-position);
      box-shadow: 0 0 0 rem(2px) var(--line-color);
      border-radius: 50%;
      transition: all 0.3s;
    }
  }

  &__image {
    width: rem(80px);
    margin: 0 auto calc(var(--spacing) * 0.6);

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border: rem(1px) solid var(--line-color);
      pointer-events: none;
      z-index: 1;
    }
  }

  &__notif {
    position: absolute;
    top: 0;
    right: 0;
    width: rem(12px);
    height: rem(12px);
    border-radius: 50%;
    background-color: var(--sun-color);
    transform: translate(50%, -50%);
    z-index: 2;

    &::before {
      --pulse-size: #{rem(26px)};

      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      transform: translate(-50%, -50%);
      background-color: var(--sun-color);
      opacity: 0.3;
      animation: pulse 1.5s infinite 4s;
    }
  }

  &--bg {
    --line-color: var(--secondary-color-4);

    border: 0;
    padding: 0;
  }

  &--image {
    --line-color: var(--secondary-color-4);

    border: 0;

    .icon--check-circle {
      --icon-width: #{rem(24px)};

      position: absolute;
      width: var(--icon-width);
      height: var(--icon-width);
      color: var(--secondary-color);
      top: calc(var(--icon-width) / -2);
      right: calc(var(--icon-width) / -2);
      transform: scale(0);
      transition: all 0.3s;
      z-index: 2;
      will-change: top bottom left right box-shadow;
    }
  }

  &--active {
    --line-color: var(--secondary-color);
    --line-position: #{rem(-4px)};

    font-weight: 700;

    .icon--check-circle {
      transform: scale(1);
    }
  }

  &--stripe {
    color: var(--secondary-color-3);

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 120%;
      height: 1px;
      background: var(--secondary-color-3);
      transform: translate(-50%, -50%) rotate(-45deg);
      transition: all 0.2s;
      z-index: 1;
    }
  }

  &.skeleton {
    &::before {
      border: 0;
      border-radius: 0;
    }
  }
}
</style>
